import { useState, useEffect } from 'react';
import { supabase } from './useSupabase';

interface CartItem {
  id: string;
  product_id: string;
  quantity: number;
  product: {
    name: string;
    price: number;
    image_url: string;
    description: string;
  };
}

export const useCart = () => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [cartCount, setCartCount] = useState(0);

  const fetchCart = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        const localCart = localStorage.getItem('cart');
        if (localCart) {
          const items = JSON.parse(localCart);
          setCartItems(items);
          setCartCount(items.reduce((sum: number, item: CartItem) => sum + item.quantity, 0));
        }
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('cart_items')
        .select(`
          id,
          product_id,
          quantity,
          products (
            name,
            price,
            image_url,
            description
          )
        `)
        .eq('user_id', user.id);

      if (error) throw error;

      const formattedItems = data?.map(item => ({
        id: item.id,
        product_id: item.product_id,
        quantity: item.quantity,
        product: item.products as any
      })) || [];

      setCartItems(formattedItems);
      setCartCount(formattedItems.reduce((sum, item) => sum + item.quantity, 0));
    } catch (error) {
      console.error('Error fetching cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async (productId: string, productData: any) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        const localCart = localStorage.getItem('cart');
        const items = localCart ? JSON.parse(localCart) : [];
        const existingItem = items.find((item: CartItem) => item.product_id === productId);

        if (existingItem) {
          existingItem.quantity += 1;
        } else {
          items.push({
            id: Math.random().toString(),
            product_id: productId,
            quantity: 1,
            product: productData
          });
        }

        localStorage.setItem('cart', JSON.stringify(items));
        await fetchCart();
        return;
      }

      const { error } = await supabase
        .from('cart_items')
        .upsert({
          user_id: user.id,
          product_id: productId,
          quantity: 1
        }, {
          onConflict: 'user_id,product_id'
        });

      if (error) throw error;
      await fetchCart();
    } catch (error) {
      console.error('Error adding to cart:', error);
    }
  };

  const updateQuantity = async (itemId: string, quantity: number) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        const localCart = localStorage.getItem('cart');
        const items = localCart ? JSON.parse(localCart) : [];
        const item = items.find((i: CartItem) => i.id === itemId);
        if (item) {
          item.quantity = quantity;
          localStorage.setItem('cart', JSON.stringify(items));
        }
        await fetchCart();
        return;
      }

      const { error } = await supabase
        .from('cart_items')
        .update({ quantity })
        .eq('id', itemId);

      if (error) throw error;
      await fetchCart();
    } catch (error) {
      console.error('Error updating quantity:', error);
    }
  };

  const removeFromCart = async (itemId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        const localCart = localStorage.getItem('cart');
        const items = localCart ? JSON.parse(localCart) : [];
        const filtered = items.filter((i: CartItem) => i.id !== itemId);
        localStorage.setItem('cart', JSON.stringify(filtered));
        await fetchCart();
        return;
      }

      const { error } = await supabase
        .from('cart_items')
        .delete()
        .eq('id', itemId);

      if (error) throw error;
      await fetchCart();
    } catch (error) {
      console.error('Error removing from cart:', error);
    }
  };

  useEffect(() => {
    fetchCart();
  }, []);

  return {
    cartItems,
    cartCount,
    loading,
    addToCart,
    updateQuantity,
    removeFromCart,
    refreshCart: fetchCart
  };
};
